/// <amd-module name="Wit/QueriesHub/Charts/Components/Configuration/ConfigureChartPanel" />

import * as React from "react";
import { ChartConfiguration, ChartConfigurationResponse, ColorConfiguration, DataServiceCapabilities } from "Tfs/Charting/Contracts/Reporting";
import * as ChartResources from "Tfs/Charting/Resources";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { ContentSize } from "VSSUI/Callout";
import { Image } from "VSSUI/Image";
import { ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader, PanelOverlay } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { IQueryService } from "Wit/Common/QueryService/IQueryService";
import { getColorDictionary, ITransformQueryResults } from "Wit/QueriesHub/Charts/Common/ChartCommon";
import { ChartPropertyMap, ChartTemplates, IChartTemplate } from "Wit/QueriesHub/Charts/Common/ChartTemplate";
import { PreviewChart } from "Wit/QueriesHub/Charts/Components/Charts/PreviewChart";
import { ChartConfigurationForm, PropertyView } from "Wit/QueriesHub/Charts/Components/Configuration/ChartConfigurationForm";
import { SeriesColorPicker } from "Wit/QueriesHub/Charts/Components/Configuration/SeriesColorPicker";
import { IQueryDisplayColumn } from "Wit/QueriesHub/Common/Interfaces";
import { IQueryChartService } from "Wit/QueriesHub/Common/Services/IQueryChartService";
import * as Resources from "Wit/QueriesHub/Resources";

export interface IConfigureChartPanelProps {
    /**
     * If this prop is provided, present as editing the chart. Otherwise present as adding a new chart.
     */
    existingConfiguration?: ChartConfiguration;
    projectId: string;
    queryId: string;
    onDismiss: (updatedChart?: ChartConfiguration) => void;
}

/**
 * Panel for adding or editing a chart.
 */
export class ConfigureChartPanel extends VssComponent<IConfigureChartPanelProps> {
    private _errorMessage = new ObservableValue("");
    private _name = new ObservableValue(Resources.NewChart);
    private _isValid = new ObservableValue(false);
    private _showOverlay = new ObservableValue(false);
    private _chartProperties = new ObservableValue<ChartPropertyMap>({});
    private _chartTypeItemProvider = new ArrayItemProvider(ChartTemplates);
    private _chartTypeSelection = new ListSelection();
    private _capabilities = new ObservableValue<DataServiceCapabilities | undefined>(undefined);
    private _queryColumns = new ObservableValue<IQueryDisplayColumn[] | undefined>(undefined);
    /** results for the preview chart */
    private _queryResults = new ObservableValue<ITransformQueryResults | undefined>(undefined);
    private _userColors = new ObservableArray<ColorConfiguration>();

    constructor(props: IConfigureChartPanelProps, context: IVssComponentContext) {
        super(props, context);
    }

    public componentDidMount() {
        super.componentDidMount();

        const existingConfiguration = this.props.existingConfiguration;
        if (existingConfiguration) {
            // load existing chart configuration
            this._name.value = existingConfiguration.title;
            const chartTypeIdx = ChartTemplates.findIndex(ct => ct.type === existingConfiguration.chartType);
            const chartTemplate = ChartTemplates[chartTypeIdx];
            this._chartTypeSelection.select(chartTypeIdx);
            const chartProperties = {} as ChartPropertyMap;
            for (const p of chartTemplate.properties) {
                chartProperties[p] = existingConfiguration.transformOptions[p] as any;
            }
            this._chartProperties.value = chartProperties;
            this._userColors.value = existingConfiguration.userColors || [];
        } else {
            // this is a new chart
            this._chartTypeSelection.select(0);
        }

        this._loadData();
    }

    private async _loadData() {
        const chartService = this.context.pageContext.getService<IQueryChartService>("IQueryChartService");
        const queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        try {
            const results = await Promise.all([chartService.getCapabilities(), queryService.getQueryColumns(this.props.projectId)]);
            this._capabilities.value = results[0];
            this._queryColumns.value = results[1];
        } catch (err) {
            console.error(err);
            this._errorMessage.value = Resources.ChartFormError;
        }
    }

    public render(): JSX.Element {
        return (
            <Observer capabilities={this._capabilities} queryColumns={this._queryColumns} showOverlay={this._showOverlay}>
                {({
                    capabilities,
                    queryColumns,
                    showOverlay
                }: {
                    capabilities: DataServiceCapabilities | undefined;
                    queryColumns: IQueryDisplayColumn[] | undefined;
                    showOverlay: boolean;
                }) => (
                    <CustomPanel
                        className="new-plan-panel"
                        lightDismiss={!showOverlay}
                        onDismiss={() => this.props.onDismiss()}
                        size={ContentSize.ExtraLarge}
                        defaultActiveElement=".name-text-field"
                    >
                        <PanelHeader
                            onDismiss={() => this.props.onDismiss()}
                            titleProps={{
                                iconProps: { iconName: "PlanView" },
                                text: Resources.ConfigureChartTitle
                            }}
                        />
                        <PanelContent className="padding-horizontal-20 flex-row flex-grow rhythm-horizontal-16">
                            {capabilities && queryColumns ? (
                                <>
                                    <div className="flex-column">
                                        <span className="margin-bottom-4">{Resources.ChartType}</span>
                                        <ScrollableList
                                            ariaLabel={Resources.ChartType}
                                            itemProvider={this._chartTypeItemProvider}
                                            selection={this._chartTypeSelection}
                                            renderRow={(index, item, details) => (
                                                <ListItem key={item.type} index={index} details={details}>
                                                    <div className="flex-row flex-center margin-vertical-4">
                                                        <Image
                                                            alt=""
                                                            className="margin-left-4"
                                                            src={getAssetLocation(
                                                                this.context.pageContext,
                                                                `ms.vss-work-web/new-queries-hub-content/Illustrations/icon-${item.type}.png`
                                                            )}
                                                        />
                                                        <span className="margin-horizontal-8">{item.label}</span>
                                                    </div>
                                                </ListItem>
                                            )}
                                        />
                                    </div>
                                    <div className="flex-column scroll-auto flex-grow rhythm-vertical-24">
                                        <Observer chartTypeSelection={this._chartTypeSelection} errorMessage={this._errorMessage}>
                                            {({
                                                chartTypeSelection,
                                                errorMessage
                                            }: {
                                                chartTypeSelection: ISelectionRange[];
                                                errorMessage: string | undefined;
                                            }) => (
                                                <>
                                                    {errorMessage && (
                                                        <div className="padding-horizontal-20 rhythm-vertical-16">
                                                            <MessageCard severity={MessageCardSeverity.Error}>{errorMessage}</MessageCard>
                                                        </div>
                                                    )}
                                                    <ChartConfigurationForm
                                                        capabilities={capabilities}
                                                        chartProperties={this._chartProperties}
                                                        chartTemplate={ChartTemplates[chartTypeSelection[0].beginIndex]}
                                                        isValid={this._isValid}
                                                        name={this._name}
                                                        projectId={this.props.projectId}
                                                        queryColumns={queryColumns}
                                                    />
                                                    <ColorPickers
                                                        chartTemplate={ChartTemplates[chartTypeSelection[0].beginIndex]}
                                                        queryResults={this._queryResults}
                                                        userColors={this._userColors}
                                                    />
                                                    <Observer
                                                        chartProperties={this._chartProperties}
                                                        isValid={this._isValid}
                                                        userColors={this._userColors}
                                                    >
                                                        {({
                                                            chartProperties,
                                                            isValid,
                                                            userColors
                                                        }: {
                                                            chartProperties: ChartPropertyMap;
                                                            isValid: boolean;
                                                            userColors: ColorConfiguration[];
                                                        }) => {
                                                            if (!isValid) {
                                                                return null;
                                                            } else {
                                                                return (
                                                                    <PreviewChart
                                                                        chartType={ChartTemplates[chartTypeSelection[0].beginIndex].type}
                                                                        chartProperties={chartProperties}
                                                                        name={this._name}
                                                                        projectId={this.props.projectId}
                                                                        queryId={this.props.queryId}
                                                                        queryResults={this._queryResults}
                                                                        userColors={userColors}
                                                                    />
                                                                );
                                                            }
                                                        }}
                                                    </Observer>
                                                </>
                                            )}
                                        </Observer>
                                    </div>
                                </>
                            ) : (
                                <Spinner className="flex-grow" />
                            )}
                        </PanelContent>
                        <PanelFooter>
                            <ButtonGroup className="bolt-panel-footer-buttons">
                                <Button text={Resources.Cancel} onClick={() => this.props.onDismiss()} />
                                <Observer isValid={this._isValid}>
                                    {({ isValid }: { isValid: boolean }) => (
                                        <Button disabled={!isValid} onClick={this._onSaveClick} primary text={Resources.SaveChart} />
                                    )}
                                </Observer>
                            </ButtonGroup>
                        </PanelFooter>
                        {showOverlay && <PanelOverlay overlayContent={<Spinner label={Resources.SavingChart} />} />}
                    </CustomPanel>
                )}
            </Observer>
        );
    }

    private _onSaveClick = async () => {
        try {
            this._showOverlay.value = true;
            const queryChartService = this.context.pageContext.getService<IQueryChartService>("IQueryChartService");
            const chartType = ChartTemplates[this._chartTypeSelection.value[0].beginIndex].type;
            const userColors = this._userColors.value;

            let updated: ChartConfigurationResponse;
            if (this.props.existingConfiguration) {
                updated = await queryChartService.editChart(
                    this.props.projectId,
                    this.props.queryId,
                    this.props.existingConfiguration.chartId,
                    this.props.existingConfiguration.transformOptions.transformId,
                    chartType,
                    this._name.value,
                    this._chartProperties.value,
                    userColors
                );
            } else {
                updated = await queryChartService.addChart(
                    this.props.projectId,
                    this.props.queryId,
                    chartType,
                    this._name.value,
                    this._chartProperties.value,
                    userColors
                );
            }

            this.props.onDismiss(updated.chartConfiguration);
        } catch (ex) {
            this._showOverlay.value = false;
            this._errorMessage.value = ex.message;
        }
    };
}

function ColorPickers(props: {
    chartTemplate: IChartTemplate;
    queryResults: IObservableValue<ITransformQueryResults | undefined>;
    userColors: IObservableArray<ColorConfiguration>;
}) {
    return (
        <Observer queryResults={props.queryResults} userColors={props.userColors}>
            {({ queryResults, userColors }: { queryResults: ITransformQueryResults | undefined; userColors: ColorConfiguration[] }) => {
                const colorDictionary = getColorDictionary(userColors);
                let seriesList: string[] | undefined;
                // AreaChart only has one color and so we have some special handling (hacks)
                const isAreaChart = props.chartTemplate.type === "AreaChart";
                if (isAreaChart) {
                    // currently always "[NULL]" but if we had support for histogram charts this will handle that too
                    seriesList = queryResults?.result[0].data?.map(d => normalizeKey(d.key));
                } else {
                    seriesList = queryResults?.result[0].data[0]?.value.map(kvp => normalizeKey(kvp.key));
                }
                if (!seriesList || seriesList.length === 0) {
                    return null;
                }
                return (
                    <PropertyView label={Resources.Series} tooltip={Resources.SeriesTooltip}>
                        {seriesList.map((series, i) => {
                            const color = colorDictionary.getColorPair(series, i);
                            return (
                                <SeriesColorPicker
                                    key={series}
                                    color={color.background}
                                    seriesName={isAreaChart ? undefined : series}
                                    onColorSelected={c => {
                                        const idx = props.userColors.value.findIndex(uc => uc.value === series);
                                        const newValue = { value: series, backgroundColor: c };
                                        if (idx >= 0) {
                                            props.userColors.splice(idx, 1, newValue);
                                        } else {
                                            props.userColors.push(newValue);
                                        }
                                    }}
                                />
                            );
                        }) || null}
                        <Button className="flex-self-start" onClick={() => props.userColors.splice(0, props.userColors.length)}>
                            {Resources.ClearCustomColors}
                        </Button>
                    </PropertyView>
                );
            }}
        </Observer>
    );
}

function normalizeKey(key: string): string {
    if (key === null || key == undefined || key === "[NULL]" || key === "NULL") {
        return ChartResources.ChartDataNullValueLabel;
    }
    return key;
}
